/**
 * DEXA Report Validation Rules
 * Admin authorization required if any of these conditions are met
 */

export const validateDEXAReport = (formData) => {
  const violations = [];

  // Rule 1: Check for limb asymmetry > 10%
  if (formData.asymmetry?.limbComposition) {
    const { rightArm, leftArm, rightLeg, leftLeg } = formData.asymmetry.limbComposition;

    // Calculate arm asymmetry
    if (rightArm?.fat && leftArm?.fat) {
      const armAsymmetry = calculateAsymmetry(rightArm.fat, leftArm.fat);
      if (armAsymmetry > 10) {
        violations.push({
          rule: 'Limb Asymmetry',
          reason: `Upper limb asymmetry ${armAsymmetry.toFixed(2)}% exceeds 10% threshold`
        });
      }
    }

    // Calculate leg asymmetry
    if (rightLeg?.fat && leftLeg?.fat) {
      const legAsymmetry = calculateAsymmetry(rightLeg.fat, leftLeg.fat);
      if (legAsymmetry > 10) {
        violations.push({
          rule: 'Limb Asymmetry',
          reason: `Lower limb asymmetry ${legAsymmetry.toFixed(2)}% exceeds 10% threshold`
        });
      }
    }
  }

  // Rule 2: Check for missing ALMI, FFMI, Body Fat %, Bone Density Z-Score, Visceral Fat Area
  const requiredFields = [
    { key: 'visceral_fat_area', label: 'Visceral Fat Area' },
    { key: 'body_fat_percentage', label: 'Body Fat %' }
  ];

  for (const field of requiredFields) {
    if (!formData[field.key] || formData[field.key] === '' || formData[field.key] === null) {
      violations.push({
        rule: 'Missing Required Values',
        reason: `${field.label} value is not available`
      });
    }
  }

  // Check Z-Scores in BMD sections
  const bmdSections = formData.bmdSections || {};
  let hasZScoreIssue = false;

  Object.keys(bmdSections).forEach(sectionKey => {
    const section = bmdSections[sectionKey];
    if (Array.isArray(section)) {
      section.forEach(row => {
        if (!row.zScore || row.zScore === '' || row.zScore === null) {
          hasZScoreIssue = true;
        }
      });
    }
  });

  if (hasZScoreIssue) {
    violations.push({
      rule: 'Missing Required Values',
      reason: 'Bone Density Z-Score value is not available'
    });
  }

  // Rule 3: Check for missing images
  const requiredImages = {
    apSpine: 'AP Spine',
    rightFemur: 'Right Femur',
    leftFemur: 'Left Femur'
  };

  const bmdImages = formData.bmdImages || {};
  Object.keys(requiredImages).forEach(imageKey => {
    if (!bmdImages[imageKey]) {
      violations.push({
        rule: 'Missing Images',
        reason: `Image for ${requiredImages[imageKey]} is not available`
      });
    }
  });

  // Check for whole body image (asymmetry image)
  if (!formData.asymmetry?.imageUrl) {
    violations.push({
      rule: 'Missing Images',
      reason: 'Image for Whole body (Body Asymmetry) is not available'
    });
  }

  // Rule 4: Check T-Scores for AP Spine, Right Femur, Left Femur
  const tScoreChecks = [
    { section: 'apSpine', label: 'AP Spine' },
    { section: 'rightFemur', label: 'Right Femur' },
    { section: 'leftFemur', label: 'Left Femur' }
  ];

  tScoreChecks.forEach(({ section, label }) => {
    const sectionData = bmdSections[section];
    if (Array.isArray(sectionData)) {
      const outOfRangeRows = [];
      sectionData.forEach(row => {
        const tScore = parseFloat(row.tScore);
        if (!isNaN(tScore)) {
          if (tScore < -3 || tScore > 2) {
            outOfRangeRows.push(`${row.region} (${tScore})`);
          }
        }
      });
      
      if (outOfRangeRows.length > 0) {
        violations.push({
          rule: 'T-Score Out of Range',
          reason: `${label}: ${outOfRangeRows.join(', ')} - outside acceptable range (-3 to 2)`
        });
      }
    }
  });

  return {
    isValid: violations.length === 0,
    violations
  };
};

/**
 * Calculate asymmetry percentage between two values
 */
const calculateAsymmetry = (value1, value2) => {
  const max = Math.max(Math.abs(value1), Math.abs(value2));
  if (max === 0) return 0;
  return ((Math.abs(value1 - value2) / max) * 100);
};

/**
 * Verify admin password
 */
export const verifyAdminPassword = (password) => {
  // Static password for now - can be updated to use environment variables
  const ADMIN_PASSWORD = 'admin@123';
  return password === ADMIN_PASSWORD;
};
